import * as React from 'react';
import moment from 'moment-timezone';
import './membershipSummaryListView.module.scss';
import { DetailedDate2, DetailedDateRange } from '../../../../../admin/components/date/TimeRange';
import { MobBr, SpBr } from '../../../../../commonv3/components/atoms/layouts/br';
import { Button } from '../../../../../commonv3/components/atoms/buttons/Button';
import { LoaderRow } from '../../../atoms/layout/LoaderRow';
import { MembershipItemId } from '../../../../../commonv3/types/membershipPlans';
import { getKey, isExpired, MembershipSummary } from '../../../../types/membershipSummaries';
import { GlobalStatesV3 } from '../../../../../commonv3/states/GlobalStatesV3';
import { V3Routes } from '../../../../routes';

const MembershipSummaryRow = ({ title, children, className = undefined }) => (
  <div styleName="membership-summary-row" className={className}>
    <div styleName="title">{title}</div>
    <div styleName="body">{children}</div>
  </div>
);

const MembershipSummaryView = ({
  summary,
  nextSummary,
  isWithdrawalSet,
  onUpdateRequest,
  onUpdateCancelRequest,
  onCancelSuspensionRequest,
}) => {
  const { subscribed } = summary;
  const future = moment(summary.continued_since).isAfter(moment());
  return (
    <div
      styleName="membership-summary"
      className="membership-summary-view"
      id={`membership-summary-view-${summary.chain_id}`}
    >
      <MembershipSummaryRow title={<span>ご利用中のプラン</span>}>
        <div styleName="current-plan">
          {summary.plan_name}
          {summary.suspended && <span styleName="suspension-status">休会中</span>}
          {subscribed && !future && !summary.suspended && !isWithdrawalSet && !nextSummary && (
            <div>
              <Button
                type="primary"
                size="s"
                className="plan-update-request-button"
                styleName="action-button"
                onClick={() => onUpdateRequest && onUpdateRequest(summary.membership_item_id)}
              >
                プランを変更する
              </Button>
              {!summary.future_suspension && <MobBr />}
              {!summary.future_suspension && (
                <Button
                  type="underline"
                  size="s"
                  className="suspension-request-button"
                  styleName="suspension-button"
                  onClick={() =>
                    GlobalStatesV3.routing.push(V3Routes.subscriptionSuspensionInfo(summary.subscription_code))
                  }
                >
                  休会手続きに進む
                </Button>
              )}
            </div>
          )}
          {subscribed && !future && summary.suspended && (
            <Button
              type="primary"
              size="s"
              styleName="action-button"
              onClick={() =>
                GlobalStatesV3.routing.push(V3Routes.subscriptionSuspensionCancelInfo(summary.subscription_code))
              }
            >
              復帰する
            </Button>
          )}
        </div>
      </MembershipSummaryRow>
      {summary.subscription_suspension && summary.future_suspension && (
        <MembershipSummaryRow title="休会予約" className="suspension-schedule-info">
          <div>
            <span styleName="suspension-period">
              <DetailedDateRange
                fromDate={summary.subscription_suspension.starts_at}
                toDate={summary.subscription_suspension.ends_at}
              />
            </span>
            での休会が予約されています
          </div>
          <div styleName="cancel-future-suspension">
            <Button
              type="normal"
              size="s"
              styleName="cancel-future-suspension-button"
              onClick={() =>
                onCancelSuspensionRequest &&
                onCancelSuspensionRequest(
                  summary.subscription_code,
                  summary.subscription_suspension.subscription_suspension_code
                )
              }
            >
              休会予約を取り消す
            </Button>
          </div>
        </MembershipSummaryRow>
      )}
      {future && (
        <MembershipSummaryRow title="受講開始日" className="start-date-info">
          <DetailedDate2 date={summary.continued_since} />
        </MembershipSummaryRow>
      )}
      {!future && (
        <MembershipSummaryRow title="受講期間" className="membership-period-info">
          {summary.strict_expires_at && (
            <DetailedDateRange fromDate={summary.continued_since} toDate={summary.strict_expires_at} />
          )}
          {!summary.strict_expires_at && <DetailedDate2 date={summary.continued_since} />}
        </MembershipSummaryRow>
      )}
      {subscribed &&
        !isWithdrawalSet &&
        summary.next_payment_date &&
        !summary.future_suspension &&
        summary.plan_item_price > 0 && (
          <MembershipSummaryRow title="次回のお支払い" className="next-payment-info">
            <div>
              <DetailedDate2 date={summary.next_payment_date} />に{summary.plan_item_price_with_tax.toLocaleString()}
              円（税込）
              <SpBr />
              を請求予定です。
            </div>
          </MembershipSummaryRow>
        )}
      {summary.subscription_suspension && !summary.future_suspension && (
        <MembershipSummaryRow title="休会期間" className="suspension-info">
          <DetailedDateRange
            fromDate={summary.subscription_suspension.starts_at}
            toDate={summary.subscription_suspension.ends_at}
          />
        </MembershipSummaryRow>
      )}
      {nextSummary && (
        <MembershipSummaryRow title="プラン変更予約">
          <div styleName="update-schedule">
            <div>
              <b>
                <DetailedDate2 date={nextSummary.continued_since} />
              </b>
              に<b>{nextSummary.plan_name}</b>
              <MobBr />
              への変更が予約されています。
            </div>
            <div id="cancel-plan-change-button" styleName="button-wrapper">
              <Button
                type="normal"
                size="s"
                styleName="action-button"
                onClick={() =>
                  onUpdateCancelRequest &&
                  onUpdateCancelRequest(summary.membership_item_id, nextSummary.membership_item_id)
                }
              >
                変更を取り消す
              </Button>
            </div>
          </div>
        </MembershipSummaryRow>
      )}
    </div>
  );
};

export function findNextSummary(summary, summaries): MembershipSummary {
  return summaries.find((s) => s.parent_chain_id == summary.chain_id);
}

type Props = {
  summaries: MembershipSummary[];
  loading?: boolean;
  isWithdrawalSet: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdateRequest?: (itemId: MembershipItemId) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdateCancelRequest?: (originalItemId: MembershipItemId, nextItemId: MembershipItemId) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onCancelSuspensionRequest?: (subscriptionCode: string, subscriptionSuspensionCode: string) => any;
};

export const getChangingPlanMembershipSummaries = (summaries) =>
  summaries
    .filter((s) => !isExpired(s))
    .map((s) => findNextSummary(s, summaries))
    .filter((s) => !!s)
    .map(getKey);

export const filterVisibleMembershipSummaries = (summaries) => {
  const changingPlanSummaries = getChangingPlanMembershipSummaries(summaries);
  return summaries.filter((s) => {
    const key = getKey(s);
    if (changingPlanSummaries.indexOf(key) >= 0) {
      return false;
    }
    if (isExpired(s)) {
      return false;
    }
    return true;
  });
};

export class MembershipSummaryListView extends React.Component<Props> {
  render() {
    const {
      summaries,
      loading = false,
      isWithdrawalSet,
      onUpdateRequest,
      onUpdateCancelRequest,
      onCancelSuspensionRequest,
    } = this.props;
    if (loading) {
      return <LoaderRow />;
    }
    return (
      <div styleName="membership-summary-list">
        {filterVisibleMembershipSummaries(summaries).map((summary) => {
          return (
            <MembershipSummaryView
              key={`${summary.plan_name}${summary.continued_since}`}
              summary={summary}
              nextSummary={findNextSummary(summary, summaries)}
              isWithdrawalSet={isWithdrawalSet}
              onUpdateRequest={onUpdateRequest}
              onUpdateCancelRequest={onUpdateCancelRequest}
              onCancelSuspensionRequest={onCancelSuspensionRequest}
            />
          );
        })}
      </div>
    );
  }
}
